import { useEffect, useState } from "react";
import { http, fmtINR, fmtNum, fmtPct } from "@/lib/api";
import {
    ReportTable,
    ReportTHead,
    SortableTH,
    FilterBar,
    FilterSelect,
    SearchInput,
} from "@/components/ReportTable";
import {
    BarChart,
    Bar,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { LayoutDashboard, ClipboardEdit } from "lucide-react";
import DsrManualUpload from "@/pages/reports/DsrManualUpload";

const TABS = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "dsr-tab-dashboard" },
    { key: "manual", label: "Manual Upload", icon: ClipboardEdit, testid: "dsr-tab-manual" },
];

export default function DsrReport() {
    const [tab, setTab] = useState("dashboard");

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6" data-testid="dsr-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Reports · DSR</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Daily Sales Report</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Store-level performance vs monthly target — upload the workbook or capture manually below.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex w-fit items-center gap-1 rounded-sm border border-border bg-card p-0.5">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={t.testid}
                        onClick={() => setTab(t.key)}
                        className={
                            "inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-[12px] font-medium transition-colors " +
                            (tab === t.key
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        <t.icon className="h-3.5 w-3.5" />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "dashboard" ? <DashboardTab /> : <DsrManualUpload />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Dashboard tab (existing DSR analytics — preserved verbatim)
// ---------------------------------------------------------------------------
function DashboardTab() {
    const [facets, setFacets] = useState({});
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState({ by: "mrp_sale", dir: -1 });
    const [filters, setFilters] = useState({ zone: "All", state: "All", rsm_asm: "All", gender: "All" });
    const [q, setQ] = useState("");

    useEffect(() => {
        http.get("/reports/dsr/facets").then((r) => setFacets(r.data));
        http.get("/reports/dsr/summary").then((r) => setSummary(r.data));
    }, []);

    useEffect(() => {
        const params = { limit: 400, sort_by: sort.by, sort_dir: sort.dir, q };
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "All") params[k] = v;
        });
        http.get("/reports/dsr", { params }).then((r) => {
            setRows(r.data.rows);
            setTotal(r.data.total);
        });
    }, [filters, sort, q]);

    const t = summary?.totals;
    const empty = t && t.stores === 0;

    return (
        <>
            <div className="flex items-center justify-end text-[11px] text-muted-foreground">
                {t ? `${fmtNum(t.stores)} stores · ${fmtNum(rows.length)}/${fmtNum(total)} shown` : "…"}
            </div>

            {empty && <Empty title="No DSR data yet" hint="Upload the DSR workbook via Reports → Upload, or add rows via the Manual Upload tab." />}

            {t && !empty && (
                <>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                        <Stat label="Stores" value={fmtNum(t.stores)} />
                        <Stat label="MRP Sale" value={fmtINR(t.mrp)} />
                        <Stat label="Net Sales" value={fmtINR(t.net)} />
                        <Stat label="Qty" value={fmtNum(t.qty)} />
                        <Stat label="MRP Ach %" value={fmtPct(t.mrp_ach_pct)} tone={t.mrp_ach_pct >= 80 ? "green" : "red"} />
                        <Stat label="Net Ach %" value={fmtPct(t.net_ach_pct)} tone={t.net_ach_pct >= 80 ? "green" : "red"} />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                        <ChartCard title="Zone-wise MRP Sale" col={5}>
                            <ResponsiveContainer>
                                <BarChart data={summary.by_zone}>
                                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                                    <XAxis dataKey="key" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} />
                                    <YAxis
                                        stroke="hsl(var(--muted-foreground))"
                                        fontSize={10}
                                        width={40}
                                        tickLine={false}
                                        tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v)}
                                    />
                                    <Tooltip
                                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                                        formatter={(v) => fmtINR(v)}
                                    />
                                    <Bar dataKey="mrp" fill="hsl(var(--chart-1))" name="MRP" radius={[3, 3, 0, 0]} />
                                    <Bar dataKey="mrp_tgt" fill="hsl(var(--chart-3))" name="Target" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Top RSM / ASM" col={7}>
                            <div className="max-h-[200px] overflow-y-auto">
                                <table className="w-full text-[11px]">
                                    <thead className="sticky top-0 bg-card text-[10px] uppercase tracking-wider text-muted-foreground">
                                        <tr>
                                            <th className="px-2 py-1 text-left">RSM / ASM</th>
                                            <th className="px-2 py-1 text-right">Stores</th>
                                            <th className="px-2 py-1 text-right">MRP</th>
                                            <th className="px-2 py-1 text-right">Net</th>
                                            <th className="px-2 py-1 text-right">Target</th>
                                            <th className="px-2 py-1 text-right">Ach %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.by_rsm.map((r) => {
                                            const pct = r.mrp_tgt ? (r.mrp / r.mrp_tgt) * 100 : 0;
                                            return (
                                                <tr key={r.key} className="border-t border-border">
                                                    <td className="px-2 py-1 font-medium">{r.key || "—"}</td>
                                                    <td className="mono-num px-2 py-1 text-right">{fmtNum(r.stores)}</td>
                                                    <td className="mono-num px-2 py-1 text-right">{fmtINR(r.mrp)}</td>
                                                    <td className="mono-num px-2 py-1 text-right">{fmtINR(r.net)}</td>
                                                    <td className="mono-num px-2 py-1 text-right text-muted-foreground">{fmtINR(r.mrp_tgt)}</td>
                                                    <td
                                                        className={
                                                            "mono-num px-2 py-1 text-right font-semibold " +
                                                            (pct >= 100 ? "text-emerald-600" : pct >= 70 ? "" : "text-red-500")
                                                        }
                                                    >
                                                        {fmtPct(pct)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </ChartCard>
                    </div>
                </>
            )}

            <FilterBar>
                <SearchInput value={q} onChange={setQ} placeholder="Search store / location / employee…" testid="dsr-search" />
                <FilterSelect label="Zone" value={filters.zone} options={facets.zones || []} onChange={(v) => setFilters({ ...filters, zone: v })} testid="dsr-filter-zone" />
                <FilterSelect label="State" value={filters.state} options={facets.states || []} onChange={(v) => setFilters({ ...filters, state: v })} testid="dsr-filter-state" />
                <FilterSelect label="RSM/ASM" value={filters.rsm_asm} options={facets.rsm_asms || []} onChange={(v) => setFilters({ ...filters, rsm_asm: v })} testid="dsr-filter-rsm" />
                <FilterSelect label="Gender" value={filters.gender} options={facets.genders || []} onChange={(v) => setFilters({ ...filters, gender: v })} testid="dsr-filter-gender" />
            </FilterBar>

            <ReportTable testid="dsr-table">
                <ReportTHead>
                    <SortableTH label="Location" field="location" sort={sort} setSort={setSort} />
                    <SortableTH label="Work Location" field="work_location" sort={sort} setSort={setSort} />
                    <SortableTH label="State" field="state" sort={sort} setSort={setSort} />
                    <SortableTH label="Zone" field="zone" sort={sort} setSort={setSort} />
                    <SortableTH label="Employee" field="employee" sort={sort} setSort={setSort} />
                    <SortableTH label="Gender" field="gender" sort={sort} setSort={setSort} />
                    <SortableTH label="RSM/ASM" field="rsm_asm" sort={sort} setSort={setSort} />
                    <SortableTH label="MRP Sale" field="mrp_sale" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Net Sale" field="net_sales" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Qty" field="qty" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="MRP Tgt" field="mrp_target" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Ach %" sort={sort} setSort={setSort} align="right" />
                </ReportTHead>
                <tbody>
                    {rows.map((r) => {
                        const pct = r.mrp_target ? (r.mrp_sale / r.mrp_target) * 100 : 0;
                        return (
                            <tr key={r.id} data-testid={`dsr-row-${r.id}`} className="border-t border-border hover:bg-muted/40">
                                <td className="px-3 py-1.5 font-medium">{r.location}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{r.work_location}</td>
                                <td className="px-3 py-1.5">{r.state}</td>
                                <td className="px-3 py-1.5">{r.zone}</td>
                                <td className="px-3 py-1.5">{r.employee}</td>
                                <td className="px-3 py-1.5">{r.gender}</td>
                                <td className="px-3 py-1.5">{r.rsm_asm}</td>
                                <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.mrp_sale)}</td>
                                <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.net_sales)}</td>
                                <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.qty)}</td>
                                <td className="mono-num px-3 py-1.5 text-right text-muted-foreground">{fmtINR(r.mrp_target)}</td>
                                <td
                                    className={
                                        "mono-num px-3 py-1.5 text-right font-semibold " +
                                        (pct >= 100 ? "text-emerald-600" : pct >= 70 ? "" : pct > 0 ? "text-red-500" : "text-muted-foreground")
                                    }
                                >
                                    {r.mrp_target ? fmtPct(pct) : "—"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </ReportTable>
        </>
    );
}

// ---------------------------------------------------------------------------
// Shared UI helpers
// ---------------------------------------------------------------------------
function Stat({ label, value, tone }) {
    const c = tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-xl font-semibold ${c}`}>{value}</div>
        </div>
    );
}

function ChartCard({ title, col, children }) {
    return (
        <div className={`col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-${col}`}>
            <div className="mlabel mb-2">{title}</div>
            <div className="h-[220px]">{children}</div>
        </div>
    );
}

function Empty({ title, hint }) {
    return (
        <div className="rounded-sm border border-dashed border-border bg-card p-8 text-center">
            <div className="text-sm font-semibold">{title}</div>
            <div className="mt-1 text-[12px] text-muted-foreground">{hint}</div>
        </div>
    );
}
